import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ReactSelect from 'react-select'
import debounce from 'debounce-promise'
import { get, isEqual, sortBy, deburr } from 'lodash'
import { FormattedHTMLMessage, injectIntl } from 'react-intl'
import { Field } from 'redux-form'
import { FieldError } from 'components/fields'
import { CircleLoader } from 'components/loaders'
import styles from './select.styl'
import { createSelector } from 'reselect'

const createSortOptions = () =>
  createSelector(
    items => items,
    items => sortBy(items, [item => deburr(item.label)])
  )

let sortOptions = null

@injectIntl
class Select extends Component {
  static propTypes = {
    updater: PropTypes.any,
    options: PropTypes.array,
    autocomplete: PropTypes.bool,
    loading: PropTypes.bool,
    labelless: PropTypes.bool,
    hasFormGroup: PropTypes.bool,
    disableRedux: PropTypes.bool,
    small: PropTypes.bool,
    searchable: PropTypes.bool,
    disabled: PropTypes.bool,
    multi: PropTypes.bool,
    clearable: PropTypes.bool,
    onInputChange: PropTypes.func,
    promise: PropTypes.func,
    intlKey: PropTypes.string,
    placeholder: PropTypes.string,
    intlKeyPlaceholder: PropTypes.string,
    onOpen: PropTypes.func,
    onSelectChange: PropTypes.func,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    intl: PropTypes.object,
    sorted: PropTypes.bool,
    isDeffered: PropTypes.bool
  }

  static defaultProps = {
    searchable: false,
    labelless: false,
    small: false,
    hasFormGroup: true,
    disableRedux: false,
    clearable: true,
    multi: false,
    placeholder: 'Select',
    intlKeyPlaceholder: 'placeholder.select',
    loading: false,
    isDeffered: false
  }

  constructor(props) {
    super(props)
    this.getOptions = debounce(this.getOptions, 500)
    sortOptions = createSortOptions()
  }

  state = {
    isAsyncLoading: false
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { disabled, updater, autocomplete, params } = this.props
    const { isAsyncLoading } = this.state

    if (
      disabled !== nextProps.disabled ||
      updater !== nextProps.updater ||
      isAsyncLoading !== nextState.isAsyncLoading
    ) {
      return true
    }

    if (autocomplete) {
      // discuss this and find a better solution
      return !isEqual(params, nextProps.params)
    }

    return true
  }

  getOptions = input => {
    if (!this.props.promise) {
      console.warn('Promise must be specified ')
      return false
    }
    this.setState({
      isAsyncLoading: true
    })
    return this.props
      .promise({
        name: input,
        ...this.props.params
      })
      .then(body => {
        this.setState({
          isAsyncLoading: false
        })
        return { options: body }
      })
      .catch(() => {
        this.setState({
          isAsyncLoading: false
        })
      })
  }

  handleChange = (field, obj) => {
    this.props.onSelectChange && this.props.onSelectChange(obj)

    if (!this.props.disableRedux) {
      const data = this.props.multi ? obj : get(obj, 'value')

      field.input.onChange(data)
    }
  }

  handleInputChange = (field, obj) => {
    if (this.props.onInputChange) {
      this.props.onInputChange(obj.value)
    }
  }

  handleOnOpen = () => {
    this.props.onOpen && this.props.onOpen()
  }

  renderField = field => {
    const {
      name,
      options,
      autocomplete,
      disabled,
      loading,
      searchable,
      small,
      intl,
      placeholder,
      intlKeyPlaceholder,
      sorted,
      multi,
      clearable,
      isDeffered
    } = this.props
    const { input, meta } = field
    const selecteDisabled = (disabled || loading) && !isDeffered
    const errorClass = meta.touched && meta.error ? 'has-error' : ''
    const smallClass = small ? styles.small : ''
    const disabledClass = selecteDisabled ? styles.disabled : ''
    const translatedPlaceholder = intlKeyPlaceholder
      ? intl.formatMessage({
          id: intlKeyPlaceholder,
          defaultMessage: placeholder
        })
      : placeholder

    const editedOptions = sorted && !autocomplete
      ? sortOptions(options)
      : options

    return (
      <div
        className={`${styles.controls} ${errorClass} ${smallClass} ${disabledClass}`}
      >
        {autocomplete &&
          <ReactSelect.Async
            value={input.value}
            onChange={val => this.handleChange(field, val)}
            className="form-control--select"
            loadOptions={this.getOptions}
            {...this.props}
            searchable
            cache={false}
            id={name}
            disabled={selecteDisabled}
            loadingPlaceholder={intl.formatMessage({
              id: 'global.loading',
              defaultMessage: 'Loading'
            })}
            noResultsText={intl.formatMessage({
              id: 'label.no_results',
              defaultMessage: 'No results'
            })}
            searchPromptText={intl.formatMessage({
              id: 'label.type_to_search',
              defaultMessage: 'Type to search'
            })}
            placeholder={translatedPlaceholder}
          />}
        {!autocomplete &&
          <ReactSelect
            value={input.value}
            onChange={val => this.handleChange(field, val)}
            onInputChange={val => this.handleInputChange(field, val)}
            className="form-control--select"
            searchable={searchable}
            disabled={selecteDisabled}
            {...this.props}
            options={editedOptions}
            id={name}
            clearable={false}
            placeholder={translatedPlaceholder}
            multi={multi}
            joinValues={multi}
            backspaceToRemoveMessage=""
            onOpen={this.handleOnOpen}
          />}
        {(input.value &&
          !selecteDisabled &&
          !multi &&
          clearable &&
          <button
            type="button"
            className={styles.btnClear}
            onClick={() => this.handleChange(field, { value: null })}
          >
            <span className={`ico ico--clear ${styles.ico}`} />
          </button>) ||
          null}
        <FieldError toShow={meta.touched && meta.error} message={meta.error} />
      </div>
    )
  }

  render() {
    const {
      label,
      name,
      labelless,
      hasFormGroup,
      intlKey,
      loading
    } = this.props
    const { isAsyncLoading } = this.state
    const srOnlyClass = labelless ? 'sr-only' : ''
    const formGroupClass = hasFormGroup ? 'form-group' : ''
    const isLoading = loading || isAsyncLoading

    return (
      <div className={formGroupClass}>
        <label
          htmlFor={name}
          className={`control-label ${!labelless && styles.controlLabel} ${srOnlyClass}`}
        >
          {intlKey
            ? <FormattedHTMLMessage id={intlKey} defaultMessage={label} />
            : label}
          {isLoading &&
            <CircleLoader extendClass={styles.labelLoader} size={20} />}
        </label>

        <Field name={name} {...this.props} component={this.renderField} />
      </div>
    )
  }
}

export default Select
